import { FC } from 'react';
import { useI18nContext } from '@i18n/i18n-react';
import useTheme from '@base/useTheme';
import { ChartTileVisualization, Layout } from '@common/api';
import { DEFAULT_CHART, GROUPED_CHARTS } from '@components/chart';
import { Divider } from '@components/layout/Divider';
import { ArrowRight, ArrowUp, ArrowDown, ArrowLeft, OffsetX, OffsetY } from '@components/icons/FarseerIcons';
import { FormField, NumberInput } from '@components/form';
import { FlexCol, FlexRow } from '@components/layout/Flex';
import { Update } from '@use-gpu/state';

export type ChartGridEditProps = {
    chart: ChartTileVisualization;
    onChange: (update: Update<ChartTileVisualization | undefined>) => void;
    canEdit: boolean;
};

export const ChartGridEdit: FC<ChartGridEditProps> = (props: ChartGridEditProps) => {
    const { chart, onChange, canEdit } = props;

    const { LL } = useI18nContext();
    const { colors } = useTheme();

    const {
        layout,
        display: { showValueAxis, showDomainAxis },
        spacing: {
            gridGapX,
            gridGapY,
            gridDomainX,
            gridDomainY,
            gridValueX,
            gridValueY,
            marginLeft,
            marginTop,
            marginRight,
            marginBottom,
        },
        series: { groupByDepth },
    } = chart;

    const canGroup = GROUPED_CHARTS.includes(chart.type);

    const isVertical = layout === Layout.Vertical;

    return (
        <FlexCol $gap={4}>
            <FormField inline label={LL.CHART_SPACING()}>
                <FlexRow $gap={8} $align="center">
                    <NumberInput
                        disabled={!canEdit}
                        value={marginLeft}
                        type="int"
                        empty={DEFAULT_CHART.spacing.marginLeft}
                        min={0}
                        step={10}
                        width={72}
                        upDown
                        prefix={<ArrowRight color={colors.neutral.w700} />}
                        onChange={(marginLeft) => onChange({ spacing: { marginLeft } })}
                    />
                    <FlexCol $gap={4}>
                        <NumberInput
                            disabled={!canEdit}
                            value={marginTop}
                            type="int"
                            empty={DEFAULT_CHART.spacing.marginTop}
                            min={0}
                            step={10}
                            width={72}
                            upDown
                            prefix={<ArrowDown color={colors.neutral.w700} />}
                            onChange={(marginTop) => onChange({ spacing: { marginTop } })}
                        />
                        <NumberInput
                            disabled={!canEdit}
                            value={marginBottom}
                            type="int"
                            empty={DEFAULT_CHART.spacing.marginBottom}
                            min={0}
                            step={10}
                            width={72}
                            upDown
                            prefix={<ArrowUp color={colors.neutral.w700} />}
                            onChange={(marginBottom) => onChange({ spacing: { marginBottom } })}
                        />
                    </FlexCol>
                    <NumberInput
                        disabled={!canEdit}
                        value={marginRight}
                        type="int"
                        empty={DEFAULT_CHART.spacing.marginRight}
                        min={0}
                        step={10}
                        width={72}
                        upDown
                        prefix={<ArrowLeft color={colors.neutral.w700} />}
                        onChange={(marginRight) => onChange({ spacing: { marginRight } })}
                    />
                </FlexRow>
            </FormField>

            {canGroup && groupByDepth ? (
                <>
                    <Divider />

                    <FormField inline label={LL.GRID_SPACING()}>
                        <FlexRow $gap={8}>
                            <NumberInput
                                disabled={!canEdit}
                                value={gridGapX}
                                type="int"
                                empty={DEFAULT_CHART.spacing.gridGapX}
                                min={0}
                                step={10}
                                width={72}
                                upDown
                                prefix={<OffsetX color={colors.neutral.w700} />}
                                onChange={(gridGapX) => onChange({ spacing: { gridGapX } })}
                            />
                            <NumberInput
                                disabled={!canEdit}
                                value={gridGapY}
                                type="int"
                                empty={DEFAULT_CHART.spacing.gridGapY}
                                min={0}
                                step={10}
                                width={72}
                                upDown
                                prefix={<OffsetY color={colors.neutral.w700} />}
                                onChange={(gridGapY) => onChange({ spacing: { gridGapY } })}
                            />
                        </FlexRow>
                    </FormField>

                    <FormField inline label={LL.AXIS_SPACING()}>
                        <FlexRow $gap={8}>
                            <NumberInput
                                value={isVertical ? gridDomainX : gridValueX}
                                type="int"
                                empty={
                                    isVertical ? DEFAULT_CHART.spacing.gridDomainX : DEFAULT_CHART.spacing.gridValueX
                                }
                                min={0}
                                step={10}
                                width={72}
                                upDown
                                prefix={<ArrowRight color={colors.neutral.w700} />}
                                disabled={
                                    !canEdit || (isVertical && !showDomainAxis) || (!isVertical && !showValueAxis)
                                }
                                onChange={(spacing) =>
                                    onChange({
                                        spacing: isVertical ? { gridDomainX: spacing } : { gridValueX: spacing },
                                    })
                                }
                            />

                            <NumberInput
                                value={isVertical ? gridValueY : gridDomainY}
                                type="int"
                                empty={
                                    isVertical ? DEFAULT_CHART.spacing.gridValueY : DEFAULT_CHART.spacing.gridDomainY
                                }
                                min={0}
                                step={10}
                                width={72}
                                upDown
                                prefix={<ArrowUp color={colors.neutral.w700} />}
                                disabled={
                                    !canEdit || (!isVertical && !showDomainAxis) || (isVertical && !showValueAxis)
                                }
                                onChange={(spacing) =>
                                    onChange({
                                        spacing: isVertical ? { gridValueY: spacing } : { gridDomainY: spacing },
                                    })
                                }
                            />
                        </FlexRow>
                    </FormField>
                </>
            ) : null}
        </FlexCol>
    );
};
